/**
 * Which origin is the dashboard and which is its Salesforce embed address.
 *
 * One build serves both addresses (same Pages project, two domains), so the
 * page decides at runtime, from its own origin, whether to boot the dashboard
 * or the embed app. The pairing also tells each side whom it may talk to: the
 * approval window posts the sign-in grant only to its embed origin, and the
 * embed page accepts a grant only from its dashboard origin.
 *
 * The deployed pairs are fixed here rather than read from Pages variables so
 * a new environment needs no out-of-repo setting to be correct. The Pages
 * Worker (`public/_worker.js`) keeps the same embed hosts in
 * `DEFAULT_EMBED_HOSTS`; `pages-worker.spec.ts` checks the two agree. Local
 * development adds one pair through `VITE_DASHBOARD_ORIGIN` and
 * `VITE_EMBED_ORIGIN` (see `.env.example`).
 */

export interface EmbedHostPair {
  dashboardOrigin: string;
  embedOrigin: string;
}

export const DEPLOYED_EMBED_HOST_PAIRS: readonly EmbedHostPair[] = [
  {
    dashboardOrigin: "https://app.upside.tech",
    embedOrigin: "https://embed-app.upside.tech",
  },
  {
    dashboardOrigin: "https://stage-app.upside.tech",
    embedOrigin: "https://stage-embed-app.upside.tech",
  },
  {
    dashboardOrigin: "https://shadow-app.upside.tech",
    embedOrigin: "https://shadow-embed-app.upside.tech",
  },
];

/** The build variables that add a local-development pair. */
export interface EmbedHostEnv {
  VITE_DASHBOARD_ORIGIN?: string;
  VITE_EMBED_ORIGIN?: string;
}

/**
 * What the current origin is: the dashboard half of a pair, the embed half,
 * or neither (a preview deployment, or a build with no pair for its host).
 */
export type EmbedHostRole =
  | { kind: "dashboard"; pair: EmbedHostPair }
  | { kind: "embed"; pair: EmbedHostPair }
  | { kind: "none" };

/** `value` if it is exactly an http(s) origin, otherwise null. */
function exactOrigin(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url.origin === value ? value : null;
  } catch {
    return null;
  }
}

/**
 * The deployed pairs plus the local-development pair, when both of its
 * variables are set to distinct, exact origins. A half-set or malformed pair
 * is ignored with a warning rather than guessed at.
 */
export function embedHostPairs(env: EmbedHostEnv): EmbedHostPair[] {
  const pairs = [...DEPLOYED_EMBED_HOST_PAIRS];
  if (!env.VITE_DASHBOARD_ORIGIN && !env.VITE_EMBED_ORIGIN) {
    return pairs;
  }
  const dashboardOrigin = exactOrigin(env.VITE_DASHBOARD_ORIGIN);
  const embedOrigin = exactOrigin(env.VITE_EMBED_ORIGIN);
  if (!dashboardOrigin || !embedOrigin || dashboardOrigin === embedOrigin) {
    console.warn(
      "Ignoring the local embed pair: VITE_DASHBOARD_ORIGIN=%s VITE_EMBED_ORIGIN=%s must be two different origins",
      env.VITE_DASHBOARD_ORIGIN,
      env.VITE_EMBED_ORIGIN,
    );
    return pairs;
  }
  pairs.push({ dashboardOrigin, embedOrigin });
  return pairs;
}

export function resolveEmbedHostRole(
  origin: string,
  pairs: readonly EmbedHostPair[],
): EmbedHostRole {
  for (const pair of pairs) {
    if (pair.embedOrigin === origin) return { kind: "embed", pair };
    if (pair.dashboardOrigin === origin) return { kind: "dashboard", pair };
  }
  return { kind: "none" };
}

/** The role of the page this code is running in. */
export function currentEmbedHostRole(): EmbedHostRole {
  return resolveEmbedHostRole(
    window.location.origin,
    embedHostPairs({
      VITE_DASHBOARD_ORIGIN: import.meta.env.VITE_DASHBOARD_ORIGIN,
      VITE_EMBED_ORIGIN: import.meta.env.VITE_EMBED_ORIGIN,
    }),
  );
}
